/**
 * Accessibility utilities for WCAG 2.1 AA compliance
 */

/**
 * Announce message to screen readers
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Focus management utilities
 */
export const focusManagement = {
  /**
   * Trap focus within an element
   */
  trapFocus: (element: HTMLElement): (() => void) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);

    // Focus first element
    firstElement?.focus();

    // Return cleanup function
    return () => {
      element.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Save and restore focus
   */
  saveFocus: (): (() => void) => {
    const activeElement = document.activeElement as HTMLElement;
    return () => {
      activeElement?.focus();
    };
  },

  /**
   * Focus first error in form
   */
  focusFirstError: (formElement: HTMLElement): void => {
    const errorElement = formElement.querySelector(
      '[aria-invalid="true"], .error'
    ) as HTMLElement;
    errorElement?.focus();
  },
};

/**
 * Keyboard navigation utilities
 */
export const keyboardNavigation = {
  /**
   * Handle arrow key navigation for lists
   */
  handleArrowKeys: (
    event: KeyboardEvent,
    items: HTMLElement[],
    currentIndex: number,
    onIndexChange: (newIndex: number) => void
  ): void => {
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = items.length - 1;
        break;
      default:
        return;
    }

    onIndexChange(newIndex);
    items[newIndex]?.focus();
  },

  /**
   * Handle escape key
   */
  handleEscape: (event: KeyboardEvent, callback: () => void): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      callback();
    }
  },

  /**
   * Handle enter and space for custom buttons
   */
  handleActivation: (event: KeyboardEvent, callback: () => void): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  },
};

/**
 * ARIA utilities
 */
export const ariaUtils = {
  /**
   * Generate unique ID for ARIA relationships
   */
  generateId: (prefix = 'aria'): string => {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Set ARIA expanded state
   */
  setExpanded: (element: HTMLElement, expanded: boolean): void => {
    element.setAttribute('aria-expanded', expanded.toString());
  },

  /**
   * Set ARIA selected state
   */
  setSelected: (element: HTMLElement, selected: boolean): void => {
    element.setAttribute('aria-selected', selected.toString());
  },

  /**
   * Set ARIA checked state
   */
  setChecked: (element: HTMLElement, checked: boolean | 'mixed'): void => {
    element.setAttribute('aria-checked', checked.toString());
  },

  /**
   * Set ARIA disabled state
   */
  setDisabled: (element: HTMLElement, disabled: boolean): void => {
    if (disabled) {
      element.setAttribute('aria-disabled', 'true');
    } else {
      element.removeAttribute('aria-disabled');
    }
  },

  /**
   * Set ARIA invalid state
   */
  setInvalid: (
    element: HTMLElement,
    invalid: boolean,
    errorId?: string
  ): void => {
    element.setAttribute('aria-invalid', invalid.toString());
    if (invalid && errorId) {
      element.setAttribute('aria-describedby', errorId);
    } else if (!invalid) {
      element.removeAttribute('aria-describedby');
    }
  },
};

/**
 * Color contrast utilities
 */
export const colorContrast = {
  /**
   * Calculate relative luminance
   */
  getLuminance: (r: number, g: number, b: number): number => {
    const rgbValues = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    // Ensure we have exactly 3 values
    if (rgbValues.length !== 3) {
      throw new Error('Invalid RGB values');
    }

    const [rs, gs, bs] = rgbValues as [number, number, number];
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Calculate contrast ratio between two colors
   */
  getContrastRatio: (
    color1: [number, number, number],
    color2: [number, number, number]
  ): number => {
    const lum1 = colorContrast.getLuminance(...color1);
    const lum2 = colorContrast.getLuminance(...color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
  },

  /**
   * Check if contrast ratio meets WCAG standards
   */
  meetsWCAG: (
    ratio: number,
    level: 'AA' | 'AAA' = 'AA',
    isLargeText = false
  ): boolean => {
    if (level === 'AAA') {
      return isLargeText ? ratio >= 4.5 : ratio >= 7;
    }
    return isLargeText ? ratio >= 3 : ratio >= 4.5;
  },
};

/**
 * Accessibility testing utilities
 */
export const a11yTesting = {
  /**
   * Check if element has accessible name
   */
  hasAccessibleName: (element: HTMLElement): boolean => {
    return !!(
      element.getAttribute('aria-label') ||
      element.getAttribute('aria-labelledby') ||
      element.textContent?.trim() ||
      (element as HTMLInputElement).placeholder
    );
  },

  /**
   * Check if interactive element is keyboard accessible
   */
  isKeyboardAccessible: (element: HTMLElement): boolean => {
    const tabIndex = element.getAttribute('tabindex');
    const isNativelyFocusable = [
      'button',
      'input',
      'select',
      'textarea',
      'a',
    ].includes(element.tagName.toLowerCase());

    return isNativelyFocusable || (tabIndex !== null && tabIndex !== '-1');
  },

  /**
   * Find accessibility issues in element
   */
  findA11yIssues: (element: HTMLElement): string[] => {
    const issues: string[] = [];

    // Check for missing alt text on images
    const images = element.querySelectorAll('img');
    images.forEach((img, index) => {
      if (!img.getAttribute('alt') && img.getAttribute('alt') !== '') {
        issues.push(`Image ${index + 1} missing alt attribute`);
      }
    });

    // Check for interactive elements without accessible names
    const interactive = element.querySelectorAll(
      'button, input, select, textarea, a, [role="button"]'
    );
    interactive.forEach((el, index) => {
      if (!a11yTesting.hasAccessibleName(el as HTMLElement)) {
        issues.push(
          `Interactive element ${index + 1} (${el.tagName}) missing accessible name`
        );
      }
    });

    // Check for form inputs without labels
    const inputs = element.querySelectorAll('input, select, textarea');
    inputs.forEach((input, index) => {
      const hasLabel =
        input.getAttribute('aria-label') ||
        input.getAttribute('aria-labelledby') ||
        element.querySelector(`label[for="${input.id}"]`);

      if (!hasLabel) {
        issues.push(`Form input ${index + 1} missing label`);
      }
    });

    return issues;
  },
};

/**
 * Screen reader utilities
 */
export const screenReader = {
  /**
   * Hide element from screen readers
   */
  hide: (element: HTMLElement): void => {
    element.setAttribute('aria-hidden', 'true');
  },

  /**
   * Show element to screen readers
   */
  show: (element: HTMLElement): void => {
    element.removeAttribute('aria-hidden');
  },

  /**
   * Make element screen reader only
   */
  srOnly: (element: HTMLElement): void => {
    element.className += ' sr-only';
  },
};

/**
 * Motion and animation utilities for accessibility
 */
export const motionUtils = {
  /**
   * Check if user prefers reduced motion
   */
  prefersReducedMotion: (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  /**
   * Apply animation with respect to user preferences
   */
  safeAnimate: (
    element: HTMLElement,
    animation: string,
    duration = '300ms'
  ): void => {
    if (!motionUtils.prefersReducedMotion()) {
      element.style.animation = `${animation} ${duration}`;
    }
  },
};
